import { useEffect, useState } from "react";
import { reconcilePoItem } from "src/services/po-api";
import { useToast } from "src/hooks/useToast";
import { Spinner } from "src/components";
import moment from "moment";

const Reconcilliation = ({ orderProductId, company_code, orderedQty, orderedFree, priceIdentifier }: any) => {
  const [poDetails, setPoDetails] = useState<any>([]);
  const [isRoundDown, setIsRoundDown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { toast } = useToast();

  const fetchPoDetails = async () => {
    setIsLoading(true);
    try {
      const response = await reconcilePoItem({
        order_product_id: orderProductId,
        company_code: company_code,
      });
      const invoiceProductMappings = response?.data?.data?.invoiceProductMappings ?? [];
      // Read the round type stamped on the PO (po_hdr.item_factor_round_type) BEFORE the flatMap, which
      // drops the po_item reference. A ROUND_DOWN PO deliberately pushed less main qty than ordered, so
      // its main-qty shortage is expected and must not be reported in the summary below.
      const isRD = invoiceProductMappings.some(
        (m: any) => m?.po_item?.purchaseOrder?.item_factor_round_type === "ROUND_DOWN"
      );
      setIsRoundDown(isRD);
      const allReconciliations = invoiceProductMappings.flatMap((item: any) => item?.po_item?.mapReconciliations ?? []);
      setPoDetails(allReconciliations);
    } catch (error: any) {
      console.log(error);
      toast({
        title: "Error",
        description: error?.response?.data?.message || "Something went wrong",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPoDetails();
  }, [orderProductId]);

  // Summary computed at the frontend from the reconciliation records + the order product's ordered
  // quantities. ROUND_DOWN suppresses the (intentional) main-qty shortage; free-qty shortage applies
  // only to scheme lines (price_identifier === 2), the only lines that carry free qty.
  const totalRecvQty = poDetails.reduce((sum: number, r: any) => sum + Number(r?.received_qty || 0), 0);
  const totalRecvFree = poDetails.reduce((sum: number, r: any) => sum + Number(r?.received_free_qty || 0), 0);
  const mainShortage = isRoundDown ? 0 : Math.max(0, Number(orderedQty || 0) - totalRecvQty);
  const freeShortage = Number(priceIdentifier) === 2 ? Math.max(0, Number(orderedFree || 0) - totalRecvFree) : 0;
  const fullyReconciled = mainShortage === 0 && freeShortage === 0;

  return (
    <div>
      {isLoading ? (
        <div className="flex justify-center items-center">
          <Spinner size="small" />
        </div>
      ) : poDetails?.length > 0 ? (
        <div className="overflow-x-scroll">
          <table className="min-w-full mt-2 border">
            <thead className="w-full">
              <tr>
                <th className="px-4 py-1 text-[.8rem] font-normal text-left text-[#B5B5B5] border">Batch</th>
                <th className="px-4 py-1 text-[.8rem] font-normal text-left text-[#B5B5B5] border">Invoice Date</th>
                <th className="px-4 py-1 text-[.8rem] font-normal text-left text-[#B5B5B5] border">Invoice No.</th>
                <th className="px-4 py-1 text-[.8rem] font-normal text-left text-[#B5B5B5] border">Company Stockist Code</th>
                <th className="px-4 py-1 text-[.8rem] font-normal text-left text-[#B5B5B5] border">PO Number</th>
                <th className="px-4 py-1 text-[.8rem] font-normal text-left text-[#B5B5B5] border">Quantity</th>
                <th className="px-4 py-1 text-[.8rem] font-normal text-left text-[#B5B5B5] border">Free Quantity</th>
              </tr>
            </thead>
            <tbody>
              {poDetails?.map((item: any) => (
                <tr key={item?.invoice_product_order_product_id} className="text-sm">
                  <td className="px-4 py-1 text-[.8rem] border">{item?.reconciliation?.batch || "-"}</td>
                  <td className="px-4 py-1 text-[.8rem] border">{moment(item?.reconciliation?.comp_invoice_date).format("DD-MM-YYYY") || "N/A"}</td>
                  <td className="px-4 py-1 text-[.8rem] border">{item?.reconciliation?.comp_inv_number || "-"}</td>
                  <td className="px-4 py-1 text-[.8rem] border">{item?.reconciliation?.stockist_cmpcode || "-"}</td>
                  <td className="px-4 py-1 text-[.8rem] border">{item?.reconciliation?.po_number || "-"}</td>
                  <td className="px-4 py-1 text-[.8rem] border">{item?.received_qty || "-"}</td>
                  <td className="px-4 py-1 text-[.8rem] border">{item?.received_free_qty || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-3 flex flex-wrap items-center gap-4">
            <p className="text-[.9rem] font-semibold border border-[#E4E4E6] rounded-lg px-2 py-1">
              Total Reconciled Qty: <span className="text-[#6551ff]">{totalRecvQty}</span>
              {" + "}
              <span className="text-[#6551ff]">{totalRecvFree}</span> (free)
            </p>
            {fullyReconciled ? (
              <p className="text-[.9rem] font-semibold">
                Status: <span className="text-[#6551ff]">Fully Reconciled</span>
              </p>
            ) : (
              <p className="text-[.9rem] font-semibold">
                Shortage:{" "}
                <span className="text-[#6551ff]">
                  {mainShortage > 0 ? `${mainShortage} qty` : ""}
                  {mainShortage > 0 && freeShortage > 0 ? " + " : ""}
                  {freeShortage > 0 ? `${freeShortage} free` : ""}
                </span>
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="mt-2 text-center text-sm text-[#B5B5B5]">No data found</div>
      )}
    </div>
  );
};

export default Reconcilliation;
